/**
 * React Hook for Web Push Notifications
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  getPushSubscription,
  subscriptionToJson,
  listenToServiceWorkerMessages,
} from '@/utils/push';

interface UsePushNotificationsOptions {
  vapidPublicKey?: string;
  autoSubscribe?: boolean;
  onSubscribed?: (subscription: PushSubscription) => void;
  onUnsubscribed?: () => void;
  onMessage?: (message: any) => void;
}

interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  subscription: PushSubscription | null;
  isLoading: boolean;
  error: string | null;
  requestPermission: () => Promise<boolean>;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  getSubscriptionJson: () => PushSubscriptionJSON | null;
}

export function usePushNotifications(
  options: UsePushNotificationsOptions = {}
): UsePushNotificationsReturn {
  const {
    vapidPublicKey,
    autoSubscribe = false,
    onSubscribed,
    onUnsubscribed,
    onMessage,
  } = options;

  const [isSupported] = useState(isPushSupported());
  const [permission, setPermission] = useState<NotificationPermission>(
    getNotificationPermission()
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported) return;

      try {
        const sub = await getPushSubscription();
        setSubscription(sub);
        setIsSubscribed(sub !== null);
      } catch (err) {
        console.error('Failed to check subscription:', err);
      }
    };

    checkSubscription();
  }, [isSupported]);

  // Listen for service worker messages
  useEffect(() => {
    if (!isSupported || !onMessage) return;

    const cleanup = listenToServiceWorkerMessages(onMessage);
    return cleanup;
  }, [isSupported, onMessage]);

  // Auto-subscribe if enabled and permission granted
  useEffect(() => {
    if (!autoSubscribe || !vapidPublicKey || !isSupported) return;
    if (isSubscribed || permission !== 'granted') return;

    const autoSub = async () => {
      await subscribe();
    };

    autoSub();
  }, [autoSubscribe, vapidPublicKey, isSupported, isSubscribed, permission]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const perm = await requestNotificationPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setError('Notification permission denied');
        return false;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Permission request failed';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return false;
    }

    if (!vapidPublicKey) {
      setError('VAPID public key not provided');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        throw new Error('Service worker registration failed');
      }

      // Request permission if not granted
      if (permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) {
          return false;
        }
      }

      // Subscribe
      const sub = await subscribeToPush(vapidPublicKey);
      if (!sub) {
        throw new Error('Subscription failed');
      }

      setSubscription(sub);
      setIsSubscribed(true);
      onSubscribed?.(sub);

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Subscription failed';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, vapidPublicKey, permission, requestPermission, onSubscribed]);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await unsubscribeFromPush();
      if (result) {
        setSubscription(null);
        setIsSubscribed(false);
        onUnsubscribed?.();
      }
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unsubscribe failed';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, onUnsubscribed]);

  /**
   * Get subscription as JSON for backend
   */
  const getSubscriptionJson = useCallback((): PushSubscriptionJSON | null => {
    if (!subscription) return null;
    return subscriptionToJson(subscription);
  }, [subscription]);

  return {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    getSubscriptionJson,
  };
}
